// ecom-view.ts — the in-mission E-Com narrative overlay DOM port (HTML/DOM UI shell
// plan, Phase 5, Form 6). Renders the canvas EcomForm (ui/ecom.ts) over the live
// canvas at the forms.ini.pp geometry, BOTH variants:
//   - SMALL (kidfEcomSmall 1041, 160×75): a top- or bottom-bar message (From/To
//     labels + the message box + an OK/More button; no portraits);
//   - LARGE (kidfEcomLarge 1040, 160×150): the full panel WITH From/To portraits.
// `nBackground` selects placement: 1=SmallBottom → bottom, 3=SmallTop → top,
// 2=Large → centered (ui/ecom.ts:45-48, SetPosition:208-230).
//
// THE TYPEWRITER (the crux) is DOM-TIMER driven (requestAnimationFrame on
// performance.now deltas) — NOT gtimm. The canvas EcomTextControl reveals on gtimm,
// which FREEZES under the story-modal sim pause (the "text stopped animating"
// regression); a browser timer runs regardless, so the DOM reveal animates while
// the sim is paused. It reproduces the canvas reveal faithfully:
//   - kcchPerSec = 40 chars/sec, delta-time corrected (ui/ecom.ts:58,442);
//   - a kctSpeechDelay = 0.5s pause at each '@' speaker start before that speaker's
//     chars begin (ui/ecom.ts:59,435-438);
//   - segments rendered in the SPEAKER COLOR + NAME prefix (ecom-message.ts);
//   - the '@X' markers are NOT counted in the revealed length (ecom-message.ts);
//   - a tap anywhere NOT on the OK button reveals the whole current page instantly
//     (ShowAll, ui/ecom.ts:294-309);
//   - the OK/"More…" button appears only once the current page is fully revealed
//     (ui/ecom.ts OnTimer:344-356);
//   - PAGING (ui/ecom.ts More:254-291): a message that overflows the box is split
//     into pages that fit (DOM measurement), each typewriter-revealed; "More…"
//     advances + re-animates; the LAST page's button is "OK" → dismiss.
//
// Display-only: the seam (host.Ecom → Ecom() → env.domEcom) carries no sim/command/
// wire change.

import type { ButtonArt, TitleArt } from '../title-art.ts';
import {
  parseEcomMessage,
  layoutReveal,
  ecomAddress,
  type ParsedEcomMessage,
} from './ecom-message.ts';
import {
  COL_FG,
  TEXT_SHADOW,
  el,
  FONT_BODY,
  buttonWidthU,
  formScale,
  at,
  makeStageButton,
  type StageButtonSize,
  rootBox,
} from '../chrome.ts';

// ui/ecom.ts:45-48 — the small/large/top/bottom background types.
export const knSmallLargeTypeSmallBottom = 1;
export const knSmallLargeTypeLarge = 2;
export const knSmallLargeTypeSmallTop = 3;

// ui/ecom.ts:58-59 — the reveal tuning (chars/sec + the per-speaker start pause).
const kcchPerSec = 40;
const kctSpeechDelayMs = 500; // kctSpeechDelay = 50 hsecs = 0.5s

const FONT = FONT_BODY;
const shadow = TEXT_SHADOW;

/** The painted geometry of one variant, in forms.ini design units (forms.ini.pp
 *  kidfEcomSmall 1041 / kidfEcomLarge 1040). x,y,w,h. */
interface EcomGeom {
  /** the form's design size (the design stage). */
  form: { w: number; h: number };
  /** the FROM address label (x,y,w). */
  from: { x: number; y: number; w: number };
  /** the TO address label (x,y,w). */
  to: { x: number; y: number; w: number };
  /** the message text box (x,y,w,h) — the typewriter renders here. */
  box: { x: number; y: number; w: number; h: number };
  /** the OK/More button (x,y,w,h). */
  ok: { x: number; y: number; w: number; h: number };
  /** the From/To portrait anchors (LARGE only) — the bitmap is drawn at its
   *  natural size at (x,y); null on SMALL. */
  fromPortrait: { x: number; y: number } | null;
  toPortrait: { x: number; y: number } | null;
}

// forms.ini.pp kidfEcomSmall 1041 (FORM 0 0 160 75): From (30,6), To (96,6),
// message (4,16,152,40), OK/More (108,60,50,12). The From/To label WIDTHS are
// widened past the authored 49/53 (the wider WI font clipped the full address e.g.
// "Olstrom@ACME" by ~30px) — From spans [30,90], To spans [98,156], on the same
// row. The To x stays at the authored ~96 (not pulled flush against From) so the
// to-address clears the "TO" baked into the backdrop with a small gap after it;
// combined with the smaller addr font this fits every address.
const SMALL: EcomGeom = {
  form: { w: 160, h: 75 },
  from: { x: 30, y: 6, w: 60 },
  to: { x: 98, y: 6, w: 58 },
  box: { x: 4, y: 16, w: 152, h: 40 },
  ok: { x: 108, y: 60, w: 50, h: 12 },
  fromPortrait: null,
  toPortrait: null,
};

// forms.ini.pp kidfEcomLarge 1040 (FORM 0 0 160 150): From (3,19), To (3,34),
// portraits (80,4)/(121,4), message (4,50,152,81), OK/More (108,135,50,12). The
// From/To label WIDTHS are widened from the authored 56/54 to 74 (up to the
// portrait at x=80) so the full address shows (it clipped by ~9px under the wider
// WI font); the From/To rows are stacked vertically left of the portraits.
const LARGE: EcomGeom = {
  form: { w: 160, h: 150 },
  from: { x: 3, y: 19, w: 74 },
  to: { x: 3, y: 34, w: 74 },
  box: { x: 4, y: 50, w: 152, h: 81 },
  ok: { x: 108, y: 135, w: 50, h: 12 },
  fromPortrait: { x: 80, y: 4 },
  toPortrait: { x: 121, y: 4 },
};

export interface EcomViewDeps {
  /** the overlay container (created by the host; this view owns its contents). */
  root: HTMLElement;
  /** the speaker the message is FROM (s_aszEmailAddrs/s_aszPortraits index). */
  fromIdx: number;
  /** the speaker the message is TO. */
  toIdx: number;
  /** the raw message text (already ExpandVars-expanded by the caller). */
  message: string;
  /** true = the LARGE panel (kidfEcomLarge, with portraits); false = SMALL bar. */
  large: boolean;
  /** the nBackground type (1=SmallBottom, 2=Large, 3=SmallTop) — positions the bar. */
  nBackground: number;
  /** the backdrop bitmap (ecomsmall/largebkgd.png) as a CSS url; absent ⇒ a flat fill. */
  backdrop?: string;
  /** the From portrait bitmap (LARGE only) as a CSS url + its natural size; null ⇒ hidden. */
  fromPortrait?: { url: string; cx: number; cy: number } | null;
  /** the To portrait bitmap (LARGE only). */
  toPortrait?: { url: string; cx: number; cy: number } | null;
  /** the 9-slice button art for the OK/More button; absent ⇒ a CSS button. */
  buttonArt?: ButtonArt;
  /** play a GUI sfx on a button tap, wired to the host SoundMgr. */
  playSfx?: (sfx: number) => void;
  /** dismiss → the host unmounts the overlay + resumes (done() → next()). Called
   *  when the LAST page's OK is pressed. */
  onClose: () => void;
}

export class EcomView {
  private readonly d: EcomViewDeps;
  private readonly geom: EcomGeom;
  /** the shared 9-slice button renderer (the small dialog fallback flavour). */
  private readonly btn: (label: string, on: () => void, size: StageButtonSize) => HTMLButtonElement;
  private readonly parsedFull: ParsedEcomMessage;
  /** the per-page parsed messages (the message split to fit the box; ui/ecom.ts More). */
  private pages: ParsedEcomMessage[] = [];
  private pageIdx = 0;
  /** the running reveal position (message chars revealed on the CURRENT page). */
  private revealed = 0;
  /** performance.now() of the last reveal step (for the delta-time char rate). */
  private lastT = 0;
  /** the per-speaker-start pause budget remaining at the current reveal head (ms). */
  private speechDelayLeft = 0;
  private raf: number | null = null;

  // the live DOM nodes the reveal updates (rebuilt per page).
  private textEl: HTMLElement | null = null;
  private okBtn: HTMLButtonElement | null = null;
  private stageEl: HTMLElement | null = null;

  constructor(d: EcomViewDeps) {
    this.d = d;
    this.geom = d.large ? LARGE : SMALL;
    const mk = makeStageButton(d);
    this.btn = (label, on, size) => mk(label, on, size, { fallback: 'dialog' });
    this.parsedFull = parseEcomMessage(d.message);

    const s = d.root.style;
    // a fixed, full-window MODAL overlay above the HUD (z=20, the in-game modal
    // layer) — pointer-events:all so the canvas behind it gets nothing while up.
    s.position = 'fixed';
    s.inset = '0';
    s.display = 'flex';
    s.zIndex = '20';
    s.color = COL_FG;
    s.fontFamily = FONT;
    s.userSelect = 'none';
    s.pointerEvents = 'all';
    s.overflow = 'hidden';
    // per the user's design preference: ALL ecoms are positioned with their VERTICAL
    // CENTER over the viewport's 1/3 line (upper third), horizontally centered. The
    // container top-aligns + centers horizontally; render() sets the stage's marginTop so
    // its centre lands at vh/3 (diverges from the canvas top/bottom/center placement).
    s.alignItems = 'flex-start';
    s.justifyContent = 'center';
    // a tap anywhere NOT on the OK button reveals the whole current page (ShowAll).
    d.root.addEventListener('pointerdown', this.onBackdropTap);
    window.addEventListener('resize', this.onResize);
    this.render();
    this.start();
  }

  private readonly onResize = (): void => {
    this.render();
  };

  /** tap NOT on a control → ShowAll the current page (ui/ecom.ts OnPenEvent:294-309); once the
   *  LAST page is fully revealed, a tap dismisses instead (the C++ auto-takedown). */
  private readonly onBackdropTap = (e: PointerEvent): void => {
    if (this.okBtn !== null && e.target instanceof Node && this.okBtn.contains(e.target)) return;
    // C++ auto-takedown (Ecom.cpp:86 SetAutoTakedown + :314 kfFrmAutoTakedown): once the LAST page
    // is fully revealed a tap NOT on the button DISMISSES (it was a no-op showAll before — forcing
    // the player to hit OK). Earlier pages / mid-reveal: a tap ShowAll's the current page.
    const page = this.pages[this.pageIdx];
    const lastPageRevealed =
      page !== undefined &&
      this.pageIdx === this.pages.length - 1 &&
      this.revealed >= page.revealLength;
    if (lastPageRevealed) {
      this.d.onClose();
      return;
    }
    this.showAll();
  };

  /** reveal the whole current page instantly (EcomTextControl.ShowAll). */
  private showAll(): void {
    const page = this.pages[this.pageIdx];
    if (page === undefined) return;
    this.revealed = page.revealLength;
    this.speechDelayLeft = 0;
    this.paintReveal();
  }

  destroy(): void {
    if (this.raf !== null) {
      cancelAnimationFrame(this.raf);
      this.raf = null;
    }
    window.removeEventListener('resize', this.onResize);
    this.d.root.removeEventListener('pointerdown', this.onBackdropTap);
  }

  // ── the design stage (the forms.ini coordinate space × the magnify) ───────────
  // Same fixed magnification family as the other in-game DOM forms (chrome.ts
  // formScale / at), at THIS variant's form size.
  private scale(): number {
    return formScale();
  }

  /** build the (whole) overlay DOM for the CURRENT page; called on first show, on
   *  resize, and on a page advance. The reveal then paints into this.textEl. */
  render(): void {
    const s = this.scale();
    const g = this.geom;
    const stage = el('div', {
      position: 'relative',
      width: `${g.form.w * s}px`,
      height: `${g.form.h * s}px`,
      backgroundImage: this.d.backdrop !== undefined ? `url(${this.d.backdrop})` : 'none',
      backgroundColor: this.d.backdrop !== undefined ? 'transparent' : 'rgb(18, 30, 33)',
      backgroundSize: '100% 100%',
      backgroundRepeat: 'no-repeat',
    });
    stage.dataset.ecomStage = '1';
    // vertical CENTER over the viewport's 1/3 line: the container top-aligns the stage, and
    // this marginTop pushes it down so (marginTop + panelHeight/2) === vh/3.
    stage.style.marginTop = `${Math.max(8, Math.round(rootBox(this.d.root).height / 3 - (g.form.h * s) / 2))}px`;

    // the From / To address labels (the WEB font, per the design preference) — the
    // "@@"→"@" collapse is applied in ecomAddress.
    stage.append(at(this.addr(ecomAddress(this.d.fromIdx), s), s, g.from.x, g.from.y, g.from.w));
    stage.append(at(this.addr(ecomAddress(this.d.toIdx), s), s, g.to.x, g.to.y, g.to.w));

    // the From / To PORTRAITS (LARGE only) — the BITMAP art at its natural size,
    // scaled by the design scale; absent/null ⇒ hidden (ui/ecom.ts:129-147).
    if (g.fromPortrait !== null && this.d.fromPortrait != null) {
      stage.append(this.portrait(this.d.fromPortrait, s, g.fromPortrait.x, g.fromPortrait.y));
    }
    if (g.toPortrait !== null && this.d.toPortrait != null) {
      stage.append(this.portrait(this.d.toPortrait, s, g.toPortrait.x, g.toPortrait.y));
    }

    // the message text box — the typewriter renders the colored+named segments here.
    const box = el('div', {
      fontFamily: FONT,
      fontSize: `${textPx(s)}px`,
      lineHeight: '1.3',
      whiteSpace: 'pre-wrap',
      wordBreak: 'break-word',
      overflow: 'hidden',
      textShadow: shadow,
    });
    box.dataset.ecomText = '1';
    this.textEl = box;
    stage.append(at(box, s, g.box.x, g.box.y, g.box.w, g.box.h));

    this.stageEl = stage;
    this.d.root.replaceChildren(stage);

    // (re)page the message to fit THIS box height — must run BEFORE the button so
    // its "More…"/"OK" label reflects the page count (the box must be in the DOM to
    // measure, hence after replaceChildren).
    this.repaginate(s);

    // the OK / More… button (the 9-slice frame + a WEB label) — hidden until the
    // current page is fully revealed (ui/ecom.ts OnTimer). The label is "More…"
    // when more pages remain, else "OK" on the last page. SIZED to its label via the
    // shared buttonWidthU (the wide WI face fills the fixed 50u box, touching the 9-slice
    // frame), then RIGHT-anchored at the canvas right edge (g.ok.x + g.ok.w = 158).
    const more = this.pageIdx < this.pages.length - 1;
    const label = more ? 'More…' : 'OK';
    const okW = buttonWidthU(label, g.ok.h, s);
    const okX = g.ok.x + g.ok.w - okW;
    const btn = this.btn(label, () => this.onOk(), { w: okW * s, h: g.ok.h * s });
    btn.dataset.ecomOk = '1';
    btn.style.visibility = 'hidden'; // appears when the page completes
    this.okBtn = btn;
    stage.append(at(btn, s, okX, g.ok.y, okW, g.ok.h));

    this.paintReveal();
  }

  /** OK/More pressed: advance to the next page (re-animate) or dismiss on the last.
   *  (The tap sfx is played by the button()'s pointerdown handler — not here, or it
   *  would double-fire.) */
  private onOk(): void {
    if (this.pageIdx < this.pages.length - 1) {
      this.pageIdx++;
      this.revealed = 0;
      this.lastT = performance.now();
      this.speechDelayLeft = this.firstSpeechDelay(this.pages[this.pageIdx]);
      this.render(); // rebuild for the new page (button label flips, box clears)
      this.start(); // re-animate the new page
    } else {
      this.destroy();
      this.d.onClose();
    }
  }

  // ── paging (ui/ecom.ts More:254-291) ──────────────────────────────────────────
  // Split the full message into pages that FIT the message box. The canvas walks
  // Font::CalcBreak line by line until the box's line count is filled; here we
  // measure the rendered text height in the actual DOM box and break greedily by
  // character so each page fits. Done ONCE (the page set is stable for a given box
  // size); a resize rebuilds and re-paginates (preserving the current page index).
  private repaginate(s: number): void {
    const text = this.textEl;
    if (text === null) {
      this.pages = [this.parsedFull];
      return;
    }
    const boxH = this.geom.box.h * s;
    const full = this.d.message;

    // measure helper: does `slice` of the raw message fit the box height?
    const fits = (slice: string): boolean => {
      text.textContent = slice;
      const fit = text.scrollHeight <= boxH + 0.5;
      return fit;
    };

    if (full.length === 0 || fits(full)) {
      this.pages = [this.parsedFull];
    } else {
      const pages: string[] = [];
      let start = 0;
      while (start < full.length) {
        // grow the page until it would overflow, then back off to the last fit.
        let lo = start + 1,
          hi = full.length,
          best = start + 1;
        while (lo <= hi) {
          const mid = (lo + hi) >> 1;
          if (fits(full.slice(start, mid))) {
            best = mid;
            lo = mid + 1;
          } else hi = mid - 1;
        }
        // prefer breaking at the last whitespace within [start,best) so a word isn't
        // split across pages (the canvas breaks on whitespace via CalcBreak).
        let brk = best;
        if (best < full.length) {
          const ws = full.lastIndexOf(' ', best - 1);
          if (ws > start) brk = ws + 1;
        }
        pages.push(full.slice(start, brk));
        start = brk;
      }
      this.pages = pages.map((p) => parseEcomMessage(p));
    }

    if (this.pageIdx >= this.pages.length) this.pageIdx = this.pages.length - 1;
    text.textContent = '';
  }

  // ── the DOM-timer typewriter ──────────────────────────────────────────────────
  /** start (or restart) the rAF reveal loop for the current page. */
  private start(): void {
    if (this.raf !== null) cancelAnimationFrame(this.raf);
    this.lastT = performance.now();
    if (this.speechDelayLeft === 0 && this.revealed === 0) {
      this.speechDelayLeft = this.firstSpeechDelay(this.pages[this.pageIdx]);
    }
    const step = (now: number): void => {
      this.raf = null;
      this.tick(now);
      const page = this.pages[this.pageIdx];
      if (page !== undefined && this.revealed < page.revealLength) {
        this.raf = requestAnimationFrame(step);
      } else {
        this.paintReveal(); // ensure the button shows on completion
      }
    };
    this.raf = requestAnimationFrame(step);
  }

  /** the per-speaker-start pause (kctSpeechDelay) that applies BEFORE the first
   *  char of a page that opens with a speaker marker. The canvas pauses at every
   *  '@'; within the reveal we carry a remaining-delay budget that resets when the
   *  head reaches a new speaker boundary. We seed it for a page that opens with a
   *  named segment so the very first speaker's name+chars wait the 0.5s. */
  private firstSpeechDelay(page: ParsedEcomMessage | undefined): number {
    if (page === undefined) return 0;
    const first = page.segments[0];
    return first !== undefined && first.name !== '' ? kctSpeechDelayMs : 0;
  }

  /** advance the reveal by the elapsed time at kcchPerSec, honoring a speaker-start
   *  pause (the canvas EcomTextControl.ShowMoreText, ui/ecom.ts:420-456). */
  private tick(now: number): void {
    const page = this.pages[this.pageIdx];
    if (page === undefined || this.revealed >= page.revealLength) return;
    let dt = now - this.lastT;

    // spend any pending speaker-start pause first (don't count it as char time).
    if (this.speechDelayLeft > 0) {
      const spend = Math.min(this.speechDelayLeft, dt);
      this.speechDelayLeft -= spend;
      dt -= spend;
      this.lastT = now;
      if (this.speechDelayLeft > 0) return; // still pausing this frame
    }

    const cch = Math.trunc((kcchPerSec * dt) / 1000);
    // accrue at least one char only when whole chars are due; otherwise leave lastT
    // unmoved so the sub-char remainder accumulates into the next frame's delta.
    if (cch <= 0) return;
    this.lastT = now;

    // reveal `cch` chars, but STOP at a speaker boundary and arm the next pause —
    // the next speaker's chars wait kctSpeechDelay (the canvas advances to the '@'
    // then delays). We find boundaries by walking the segment char counts.
    const before = this.revealed;
    let target = Math.min(page.revealLength, this.revealed + cch);
    const nextBoundary = this.nextSpeakerBoundary(page, this.revealed);
    if (nextBoundary !== null && target > nextBoundary) {
      target = nextBoundary; // reveal up TO the boundary this frame
    }
    this.revealed = target;
    if (nextBoundary !== null && this.revealed === nextBoundary && this.revealed > before) {
      this.speechDelayLeft = kctSpeechDelayMs; // pause before the next speaker
    }
    this.paintReveal();
  }

  /** the message-char index at which the NEXT named speaker (after `pos`) begins —
   *  i.e. the start of the first named segment whose start > pos; null if none. */
  private nextSpeakerBoundary(page: ParsedEcomMessage, pos: number): number | null {
    let start = 0;
    for (let i = 0; i < page.segments.length; i++) {
      const seg = page.segments[i];
      if (i > 0 && seg.name !== '' && start > pos) return start;
      start += seg.text.length;
    }
    return null;
  }

  /** paint the current page into the message box + show the OK/More button once the page
   *  is fully revealed. The WHOLE page text is laid out every frame (the not-yet-revealed
   *  characters merely INVISIBLE) so the wrapping is fixed from the first frame and the
   *  streaming text never reflows/jumps lines — see buildRevealFragment. */
  private paintReveal(): void {
    const text = this.textEl;
    const page = this.pages[this.pageIdx];
    if (text === null || page === undefined) return;
    text.replaceChildren(buildRevealFragment(page, this.revealed));
    if (this.okBtn !== null) {
      this.okBtn.style.visibility = this.revealed >= page.revealLength ? 'visible' : 'hidden';
    }
  }

  // ── widgets ───────────────────────────────────────────────────────────────────
  // a From/To address label (the web font). Sized a notch smaller than the body
  // (5×s vs 5.5×s) so the longest addresses (e.g. "Olstrom@ACME", the 13-char
  // "ACME Security"/"OMNI Security") fit the From/To slots under the wider WI font.
  private addr(textStr: string, s: number): HTMLElement {
    const e = el('div', {
      fontFamily: FONT,
      fontSize: `${Math.max(6, Math.round(5 * s))}px`,
      color: COL_FG,
      textShadow: shadow,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    });
    e.textContent = textStr;
    return e;
  }

  // a portrait IMG (the bitmap art) at design coords, scaled by the design scale.
  private portrait(
    p: { url: string; cx: number; cy: number },
    s: number,
    x: number,
    y: number,
  ): HTMLElement {
    // the portrait bitmap is in GAME px (the atlas is ~2 game px per design unit — the
    // SAME 2× baked into the stage scale s = 2*gs). The stage POSITIONS in design units
    // × s, but the bitmap SIZE is game px, so it must be scaled by gs (= s/2), NOT s —
    // else it renders 2× too big and overflows the ~40-unit (80,4)/(121,4) portrait slot
    // (the canvas BitmapControl blits it 1:1 at GetSize() game px, presented × gs).
    const g = s / 2;
    const img = el('img', {
      position: 'absolute',
      left: `${x * s}px`,
      top: `${y * s}px`,
      width: `${p.cx * g}px`,
      height: `${p.cy * g}px`,
      imageRendering: 'pixelated',
    }) as HTMLImageElement;
    img.src = p.url;
    img.dataset.ecomPortrait = '1';
    return img;
  }
}

/** Build the message-box content for a parsed page revealed to `revealed` chars. Unlike a
 *  naive typewriter (which appends only the revealed substring, so a partial word at the
 *  line edge REFLOWS — jumps to the next line — the moment it grows too long to fit), this
 *  emits the WHOLE page text every frame and renders the not-yet-revealed characters in the
 *  invisible style (`visibility:hidden`, which STILL reserves their layout boxes). The line
 *  breaks are therefore computed on the full text and stay put: the reveal is just the
 *  visible↔invisible boundary marching forward, and the text never jumps lines as it
 *  streams. Shared by the E-Com and the cutscene typewriters (cutscene-view reuses it). */
export function buildRevealFragment(parsed: ParsedEcomMessage, revealed: number): DocumentFragment {
  const frag = document.createDocumentFragment();
  for (const seg of layoutReveal(parsed, revealed)) {
    if (seg.name !== '') frag.append(revealSpan(seg.color, seg.name, seg.nameVisible));
    const vis = seg.text.slice(0, seg.visibleCount);
    const hid = seg.text.slice(seg.visibleCount);
    if (vis !== '') frag.append(revealSpan(seg.color, vis, true));
    if (hid !== '') frag.append(revealSpan(seg.color, hid, false));
  }
  return frag;
}

/** A colored text run, drawn or rendered in the invisible style. Hidden = `visibility:hidden`
 *  (NOT display:none) so the run still OCCUPIES its layout box — the whole point: the line
 *  breaks are fixed by the full text regardless of how much is revealed. */
function revealSpan(color: string, text: string, visible: boolean): HTMLSpanElement {
  const span = document.createElement('span');
  span.style.color = color;
  if (!visible) span.style.visibility = 'hidden';
  span.textContent = text;
  return span;
}

// the message-box font size in CSS px for a design scale (the web font, sized for
// the small box ~5.5 design units tall ≈ a glyph per design unit). Dropped 1 pt (6→5
// design units) so longer E-Com messages stop wrapping onto an extra page.
function textPx(s: number): number {
  return Math.max(7, Math.round(5 * s));
}

/** the portrait helper the host uses: a TitleArt + its data URL → the view's
 *  {url, cx, cy}. Kept here so the host doesn't reach into the geometry. */
export function portraitFromArt(
  art: TitleArt | null,
  url: string | null,
): { url: string; cx: number; cy: number } | null {
  if (art === null || url === null) return null;
  return { url, cx: art.cx, cy: art.cy };
}
